/**
 * HelpOverlayContext — open/close state + Sidebar Help-button ref for the
 * Day 60c Help overlay (320px slide-in from right; persists open across
 * navigation within session per COMPONENT_STANDARDS §4).
 *
 * helpButtonRef is exposed so the overlay's onClose can return focus to the
 * Help button after dismiss (UI_STANDARDS §23 accessibility). Sidebar
 * attaches the ref to both branches of its collapsed/expanded Help button
 * ternary; only one is mounted at a time, so the ref always points to the
 * currently-rendered button.
 */
/* eslint-disable react-refresh/only-export-components -- Provider + context
 * are intentionally co-located here. Same pattern as DebugPanelContext.tsx
 * and useSessionTabs.tsx (CLAUDE_REFERENCE.md §6 TS migration patterns). The
 * consumer hook useHelpOverlay lives in its own file (src/hooks/useHelpOverlay.ts)
 * so Fast Refresh remains effective for the overlay + tab components.
 */
import {
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
  type JSX,
  type MutableRefObject,
  type ReactNode,
} from 'react';

/** The Help overlay tabs. Docs graduated Day 100b.2 (rendered first + default
 *  active); Shortcuts / Legend / Feedback shipped Day 60c. */
export type HelpTab = 'docs' | 'shortcuts' | 'legend' | 'feedback';

/** open() options — deep-link the overlay to a tab and (for Docs) a doc slug.
 *  No args opens the Docs tab with no slug (the general browse/search entry). */
interface HelpOverlayOpenOptions {
  tab?: HelpTab;
  slug?: string | null;
}

export interface HelpOverlayContextValue {
  isOpen: boolean;
  /** Active tab — lifted into context (Day 100b.2) so open({ tab }) can deep-link
   *  it; the overlay's tab strip reads/writes it via setActiveTab. */
  activeTab: HelpTab;
  /** Active Docs-tab slug — the contextual "?" deep-link + Related/search nav.
   *  null = the Docs tab's browse view. */
  activeSlug: string | null;
  open: (opts?: HelpOverlayOpenOptions) => void;
  close: () => void;
  toggle: () => void;
  setActiveTab: (tab: HelpTab) => void;
  setActiveSlug: (slug: string | null) => void;
  /** Ref to the Sidebar Help button — overlay's onClose returns focus to it
   *  before unmounting per UI_STANDARDS §23. Sidebar attaches via
   *  ref={helpButtonRef} on its single Help <button> (only one of the
   *  collapsed/expanded ternary branches renders at a time). */
  helpButtonRef: MutableRefObject<HTMLButtonElement | null>;
}

export const HelpOverlayContext = createContext<HelpOverlayContextValue | null>(null);

interface HelpOverlayProviderProps {
  children: ReactNode;
}

export function HelpOverlayProvider({ children }: HelpOverlayProviderProps): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<HelpTab>('docs');
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const helpButtonRef = useRef<HTMLButtonElement | null>(null);

  // Docs-first default: open() with no args lands on the Docs tab with no slug
  // (the general browse/search entry — the Sidebar Help button's behavior). A
  // deep-link (the TopBar "?" / a search result) passes { tab, slug }.
  const open = useCallback((opts?: HelpOverlayOpenOptions): void => {
    setActiveTab(opts?.tab ?? 'docs');
    setActiveSlug(opts?.slug ?? null);
    setIsOpen(true);
  }, []);
  const close = useCallback((): void => {
    setIsOpen(false);
    // Focus return runs after the overlay unmounts. requestAnimationFrame
    // gives React a tick to remove the overlay's DOM nodes before we shift
    // focus, avoiding a brief flicker where the button gets focus while the
    // overlay is still partially rendered.
    requestAnimationFrame(() => {
      helpButtonRef.current?.focus();
    });
  }, []);
  const toggle = useCallback((): void => setIsOpen((v) => !v), []);

  const value = useMemo<HelpOverlayContextValue>(
    () => ({
      isOpen,
      activeTab,
      activeSlug,
      open,
      close,
      toggle,
      setActiveTab,
      setActiveSlug,
      helpButtonRef,
    }),
    [isOpen, activeTab, activeSlug, open, close, toggle]
  );

  return <HelpOverlayContext.Provider value={value}>{children}</HelpOverlayContext.Provider>;
}
